import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";

function ExpenseOverheadListPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/expense-overhead")) return "/settings-master/expense-overhead";
    return "/expense-overhead";
  }, [location.pathname]);

  const endpoints = useMemo(
    () => ({
      list: "/expense-overhead",
      delete: (id) => `/expense-overhead/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    error: listError,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
  } = useCrud({
    endpoints,
    idKey: "expenseOverheadId",
    initialFilters: { search: "" },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) => navigate(`${basePath}/${encrypt(item?.expenseOverheadId)}/edit`);

  const tableColumns = useMemo(
    () => [
      { key: "overheadDetails", label: "Overhead Details", sortable: true },
      {
        key: "price",
        label: "Price",
        sortable: true,
        render: (item) => (
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            {item?.price ? Number(item.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
          </span>
        ),
      },
      { key: "remark", label: "Remark", sortable: true },
      {
        key: "file",
        label: "Attachment",
        render: (item) => {
          const fileUrl = item?.fileLink || item?.file || item?.fileUrl;
          if (!fileUrl) return <span className="text-slate-400 text-xs">No file</span>;
          return (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-bold text-primary-600 hover:text-primary-700 hover:underline"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View
            </a>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            Expense <span className="text-primary-600">Overhead</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Manage and track expense overheads and attachments
          </p>
        </div>
        <Button onClick={openCreate} className="shadow-xl shadow-primary-500/10">
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Overhead
        </Button>
      </div>

      {listError ? <ErrorAlert message={listError} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search overhead details..."
        tableScrollHeight="58vh"
      />
    </div>
  );
}

export default ExpenseOverheadListPage;
